import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthenticationStore } from '@/features/auth/stores/authenticationStore.js';
import { useUserProfileStore } from '@/features/user-profile/stores/userProfileStore.js';
import { useClient } from '@/plugins/api.js';

export const organizationPermissions = {
    manageOrganizationSettings: 'Organizations.Write',
    manageOrganizationMembers: 'Organizations.Write',
    createWorkspaces: 'Workspaces.Write',
    manageWorkspaces: 'Workspaces.Write',
    manageBilling: 'Billing.Write',
    manageConnectors: 'Connectors.Write',
    accessOwnedWorkspaces: 'Workspaces.Read',
};

export const accessRelationshipTypes = ['OrganizationMember', 'ExternalCollaborator', 'Client'];

export const accessRoleKeys = ['administrator', 'contributor', 'writer', 'reviewer', 'guest'];

export const useOrganizationStore = defineStore('organization', () => {
    const auth = useAuthenticationStore();
    const userProfileStore = useUserProfileStore();
    const client = useClient();

    const organizations = ref([]);
    const selectedOrganizationId = ref(null);
    const hasAppliedDefaultOrganization = ref(false);
    const detailsById = ref({});
    const isLoading = ref(false);
    const isLoadingDetails = ref(false);
    const isCreating = ref(false);
    const isSaving = ref(false);
    const isSettingDefault = ref(false);
    const isAddingMember = ref(false);
    const isUpdatingMember = ref(false);
    const isRemovingMember = ref(false);
    const isInvitingMember = ref(false);
    const isAcceptingInvite = ref(false);
    const isCancellingInvite = ref(false);
    const isResendingInvite = ref(false);
    const isUploadingLogo = ref(false);
    const isDeleting = ref(false);
    const error = ref(null);

    const activeOrganization = computed(() => {
        const organization =
            organizations.value.find(candidate => candidate.id === selectedOrganizationId.value) ?? null;
        const details = selectedOrganizationId.value ? detailsById.value[selectedOrganizationId.value] : null;

        return organization || details ? { ...(organization ?? {}), ...(details ?? {}) } : null;
    });
    const defaultOrganizationId = computed(() => userProfileStore.defaultOrganizationId);

    function userOwnsOrganization(organization) {
        return Boolean(
            organization?.ownerUserId &&
            auth.userId &&
            String(organization.ownerUserId).toLowerCase() === String(auth.userId).toLowerCase()
        );
    }

    function syncOrganizationOwnership() {
        auth.setOwnedOrganizationIds(
            organizations.value
                .filter(organization => userOwnsOrganization(organization))
                .map(organization => organization.id)
        );
        auth.setSelectedOrganizationOwnerId(activeOrganization.value?.ownerUserId ?? null);
    }

    function userCan(organization, permission) {
        return Boolean(organization?.currentUserPermissions?.includes(permission));
    }

    function setSelectedOrganization(organizationId) {
        if (organizations.value.some(organization => organization.id === organizationId)) {
            selectedOrganizationId.value = organizationId;
        }
    }

    function selectDefaultOrganizationIfAvailable({ replaceCurrent = false } = {}) {
        if (
            defaultOrganizationId.value &&
            (replaceCurrent || !selectedOrganizationId.value) &&
            organizations.value.some(organization => organization.id === defaultOrganizationId.value)
        ) {
            selectedOrganizationId.value = defaultOrganizationId.value;
            return true;
        }

        return false;
    }

    function selectDefaultOrganizationOnce() {
        if (hasAppliedDefaultOrganization.value) {
            return false;
        }

        hasAppliedDefaultOrganization.value = true;
        return selectDefaultOrganizationIfAvailable({ replaceCurrent: true });
    }

    function setSelectedOrganizationFromWorkspace(workspace) {
        if (workspace?.organizationId) {
            if (
                organizations.value.length === 0 ||
                organizations.value.some(organization => organization.id === workspace.organizationId)
            ) {
                selectedOrganizationId.value = workspace.organizationId;
            }
        }
    }

    async function fetchOrganizations() {
        if (!auth.isAuthenticated) {
            organizations.value = [];
            selectedOrganizationId.value = null;
            detailsById.value = {};
            error.value = null;
            return [];
        }

        isLoading.value = true;
        error.value = null;

        try {
            const response = await client.get('/api/organizations');
            organizations.value = response.data ?? [];

            if (
                !selectDefaultOrganizationIfAvailable() &&
                !organizations.value.some(organization => organization.id === selectedOrganizationId.value)
            ) {
                selectedOrganizationId.value = organizations.value[0]?.id ?? null;
            }

            syncOrganizationOwnership();
            return organizations.value;
        } catch (fetchError) {
            console.error('Failed to fetch organizations:', fetchError);
            organizations.value = [];
            selectedOrganizationId.value = null;
            error.value = 'Failed to load organizations.';
            return [];
        } finally {
            isLoading.value = false;
        }
    }

    async function fetchOrganization(organizationId) {
        if (!auth.isAuthenticated || !organizationId) {
            return null;
        }

        isLoadingDetails.value = true;
        error.value = null;

        try {
            const response = await client.get(`/api/organizations/${organizationId}`);
            if (response.data) {
                detailsById.value = {
                    ...detailsById.value,
                    [organizationId]: response.data,
                };
                selectedOrganizationId.value = organizationId;
                syncOrganizationOwnership();
            }

            return response.data ?? null;
        } catch (fetchError) {
            console.error('Failed to fetch organization:', fetchError);
            error.value = 'Failed to load organization.';
            return null;
        } finally {
            isLoadingDetails.value = false;
        }
    }

    async function createOrganization(payload) {
        if (!auth.isAuthenticated) {
            throw new Error('You must be authenticated to create an organization.');
        }

        if (isCreating.value) {
            throw new Error('An organization creation request is already in progress.');
        }

        isCreating.value = true;
        error.value = null;

        try {
            const response = await client.post('/api/organizations', payload);
            const organization = response.data;

            if (organization) {
                organizations.value = [...organizations.value, organization].sort((left, right) =>
                    left.name.localeCompare(right.name)
                );
                selectedOrganizationId.value = organization.id;
                syncOrganizationOwnership();
            }

            return organization;
        } catch (createError) {
            console.error('Failed to create organization:', createError);
            error.value = 'Failed to create organization.';
            throw createError;
        } finally {
            isCreating.value = false;
        }
    }

    async function updateOrganization(organizationId, payload) {
        if (!auth.isAuthenticated || !organizationId) {
            throw new Error('You must be authenticated to update an organization.');
        }

        isSaving.value = true;
        error.value = null;

        try {
            const response = await client.put(`/api/organizations/${organizationId}`, payload);
            const organization = response.data;

            if (organization) {
                const currentDetails = detailsById.value[organizationId];
                detailsById.value = {
                    ...detailsById.value,
                    [organizationId]: {
                        ...(currentDetails ?? {}),
                        ...organization,
                        members: currentDetails?.members ?? organization.members ?? [],
                        invites: currentDetails?.invites ?? organization.invites ?? [],
                        workspaces: currentDetails?.workspaces ?? organization.workspaces ?? [],
                    },
                };
                organizations.value = organizations.value.map(candidate =>
                    candidate.id === organizationId ? { ...candidate, ...organization } : candidate
                );
                syncOrganizationOwnership();
            }

            return organization;
        } catch (updateError) {
            console.error('Failed to update organization:', updateError);
            error.value = 'Failed to update organization.';
            throw updateError;
        } finally {
            isSaving.value = false;
        }
    }

    async function deleteOrganization(organizationId, confirmationName) {
        if (!auth.isAuthenticated || !organizationId) {
            throw new Error('You must be authenticated to delete an organization.');
        }

        if (isDeleting.value) {
            throw new Error('An organization deletion request is already in progress.');
        }

        isDeleting.value = true;
        error.value = null;

        try {
            await client.delete(`/api/organizations/${organizationId}`, {
                data: {
                    confirmationName,
                },
            });

            organizations.value = organizations.value.filter(organization => organization.id !== organizationId);
            const nextDetailsById = { ...detailsById.value };
            delete nextDetailsById[organizationId];
            detailsById.value = nextDetailsById;

            if (selectedOrganizationId.value === organizationId) {
                selectedOrganizationId.value = organizations.value[0]?.id ?? null;
            }

            syncOrganizationOwnership();
        } catch (deleteError) {
            console.error('Failed to delete organization:', deleteError);
            error.value = 'Failed to delete organization.';
            throw deleteError;
        } finally {
            isDeleting.value = false;
        }
    }

    async function setDefaultOrganization(organizationId) {
        if (!auth.isAuthenticated) {
            throw new Error('You must be authenticated to set a default organization.');
        }

        if (organizationId && !organizations.value.some(organization => organization.id === organizationId)) {
            throw new Error('You can only set an accessible organization as default.');
        }

        isSettingDefault.value = true;

        try {
            const nextDefaultOrganizationId = await userProfileStore.changeDefaultOrganization(organizationId ?? null);
            if (nextDefaultOrganizationId) {
                setSelectedOrganization(nextDefaultOrganizationId);
            }

            return nextDefaultOrganizationId;
        } finally {
            isSettingDefault.value = false;
        }
    }

    async function addMember(organizationId, payload) {
        if (!auth.isAuthenticated || !organizationId) {
            throw new Error('You must be authenticated to add an organization member.');
        }

        isAddingMember.value = true;
        error.value = null;

        try {
            const response = await client.post(`/api/organizations/${organizationId}/members`, payload);
            const member = response.data;

            if (member) {
                const currentDetails = detailsById.value[organizationId];
                if (currentDetails) {
                    detailsById.value = {
                        ...detailsById.value,
                        [organizationId]: {
                            ...currentDetails,
                            members: [...(currentDetails.members ?? []), member],
                        },
                    };
                }
            }

            return member;
        } catch (addError) {
            console.error('Failed to add organization member:', addError);
            error.value = 'Failed to add organization member.';
            throw addError;
        } finally {
            isAddingMember.value = false;
        }
    }

    async function inviteMember(organizationId, payload) {
        if (!auth.isAuthenticated || !organizationId) {
            throw new Error('You must be authenticated to invite an organization member.');
        }

        isInvitingMember.value = true;
        error.value = null;

        try {
            const response = await client.post(`/api/organizations/${organizationId}/invites`, payload);
            const invite = response.data;

            if (invite) {
                const currentDetails = detailsById.value[organizationId];
                if (currentDetails) {
                    detailsById.value = {
                        ...detailsById.value,
                        [organizationId]: {
                            ...currentDetails,
                            invites: [...(currentDetails.invites ?? []), invite],
                        },
                    };
                }
            }

            return invite;
        } catch (inviteError) {
            console.error('Failed to invite organization member:', inviteError);
            error.value = 'Failed to invite organization member.';
            throw inviteError;
        } finally {
            isInvitingMember.value = false;
        }
    }

    async function updateMember(organizationId, userId, payload) {
        if (!auth.isAuthenticated || !organizationId || !userId) {
            throw new Error('You must be authenticated to update an organization member.');
        }

        isUpdatingMember.value = true;
        error.value = null;

        try {
            const response = await client.patch(`/api/organizations/${organizationId}/members/${userId}`, payload);
            const member = response.data;

            if (member) {
                const currentDetails = detailsById.value[organizationId];
                if (currentDetails) {
                    detailsById.value = {
                        ...detailsById.value,
                        [organizationId]: {
                            ...currentDetails,
                            members: (currentDetails.members ?? []).map(candidate =>
                                candidate.userId === userId ? member : candidate
                            ),
                        },
                    };
                }
            }

            return member;
        } catch (updateError) {
            console.error('Failed to update organization member:', updateError);
            error.value = 'Failed to update organization member.';
            throw updateError;
        } finally {
            isUpdatingMember.value = false;
        }
    }

    async function removeMember(organizationId, userId) {
        if (!auth.isAuthenticated || !organizationId || !userId) {
            throw new Error('You must be authenticated to remove an organization member.');
        }

        isRemovingMember.value = true;
        error.value = null;

        try {
            await client.delete(`/api/organizations/${organizationId}/members/${userId}`);

            const currentDetails = detailsById.value[organizationId];
            if (currentDetails) {
                detailsById.value = {
                    ...detailsById.value,
                    [organizationId]: {
                        ...currentDetails,
                        members: (currentDetails.members ?? []).filter(member => member.userId !== userId),
                    },
                };
            }
        } catch (removeError) {
            console.error('Failed to remove organization member:', removeError);
            error.value = 'Failed to remove organization member.';
            throw removeError;
        } finally {
            isRemovingMember.value = false;
        }
    }

    async function resolveInvite(inviteId) {
        if (!inviteId) {
            throw new Error('An invite id is required.');
        }

        const response = await client.get(`/api/organization-invites/accept/${inviteId}`);
        return response.data;
    }

    async function acceptInvite(inviteId) {
        if (!auth.isAuthenticated || !inviteId) {
            throw new Error('You must be authenticated to accept an organization invite.');
        }

        if (isAcceptingInvite.value) {
            throw new Error('An organization invite acceptance request is already in progress.');
        }

        isAcceptingInvite.value = true;

        try {
            const response = await client.post(`/api/organization-invites/${inviteId}/accept`);
            const acceptedInvite = response.data;
            const organizationId = acceptedInvite?.organizationId;

            if (organizationId) {
                const currentDetails = detailsById.value[organizationId];
                if (currentDetails) {
                    detailsById.value = {
                        ...detailsById.value,
                        [organizationId]: {
                            ...currentDetails,
                            invites: (currentDetails.invites ?? []).filter(invite => invite.id !== inviteId),
                        },
                    };
                }
            }

            return acceptedInvite;
        } catch (acceptError) {
            console.error('Failed to accept organization invite:', acceptError);
            throw acceptError;
        } finally {
            isAcceptingInvite.value = false;
        }
    }

    async function cancelInvite(inviteId) {
        if (!auth.isAuthenticated || !inviteId) {
            throw new Error('You must be authenticated to cancel an organization invite.');
        }

        if (isCancellingInvite.value) {
            throw new Error('An organization invite cancellation request is already in progress.');
        }

        isCancellingInvite.value = true;

        try {
            const response = await client.post(`/api/organization-invites/${inviteId}/cancel`);
            const cancelledInvite = response.data;
            const organizationId = cancelledInvite?.organizationId ?? selectedOrganizationId.value;

            if (organizationId) {
                const currentDetails = detailsById.value[organizationId];
                if (currentDetails) {
                    detailsById.value = {
                        ...detailsById.value,
                        [organizationId]: {
                            ...currentDetails,
                            invites: (currentDetails.invites ?? []).filter(invite => invite.id !== inviteId),
                        },
                    };
                }
            }

            return cancelledInvite;
        } catch (cancelError) {
            console.error('Failed to cancel organization invite:', cancelError);
            throw cancelError;
        } finally {
            isCancellingInvite.value = false;
        }
    }

    async function resendInvite(inviteId) {
        if (!inviteId) {
            throw new Error('An invite id is required.');
        }

        if (isResendingInvite.value) {
            throw new Error('An organization invite resend request is already in progress.');
        }

        isResendingInvite.value = true;
        error.value = null;

        try {
            const response = await client.post(`/api/organization-invites/${inviteId}/resend`);
            return response.data;
        } catch (resendError) {
            console.error('Failed to resend organization invite:', resendError);
            error.value = 'Failed to resend organization invite.';
            throw resendError;
        } finally {
            isResendingInvite.value = false;
        }
    }

    async function uploadLogo(organizationId, file) {
        if (!auth.isAuthenticated || !organizationId) {
            throw new Error('You must be authenticated to upload an organization logo.');
        }

        if (isUploadingLogo.value) {
            throw new Error('An organization logo upload is already in progress.');
        }

        isUploadingLogo.value = true;
        error.value = null;

        try {
            const formData = new FormData();
            formData.append('file', file, file.name || 'organization-logo.png');

            const response = await client.post(`/api/organizations/${organizationId}/logo`, formData);
            const blobUrl = response.data?.blobUrl;

            if (blobUrl) {
                const logoUrl = `${blobUrl}?${Date.now()}`;
                const currentDetails = detailsById.value[organizationId];
                if (currentDetails) {
                    detailsById.value = {
                        ...detailsById.value,
                        [organizationId]: {
                            ...currentDetails,
                            logoUrl,
                        },
                    };
                }
                organizations.value = organizations.value.map(organization =>
                    organization.id === organizationId ? { ...organization, logoUrl } : organization
                );
            }

            return response.data;
        } catch (uploadError) {
            console.error('Failed to upload organization logo:', uploadError);
            error.value = 'Failed to upload organization logo.';
            throw uploadError;
        } finally {
            isUploadingLogo.value = false;
        }
    }

    watch(
        () => auth.isAuthenticated,
        async isAuthenticated => {
            if (!isAuthenticated) {
                organizations.value = [];
                selectedOrganizationId.value = null;
                hasAppliedDefaultOrganization.value = false;
                detailsById.value = {};
                error.value = null;
                syncOrganizationOwnership();
                return;
            }

            await fetchOrganizations();
        },
        { immediate: true }
    );

    watch(activeOrganization, () => {
        syncOrganizationOwnership();
    });

    return {
        organizations,
        selectedOrganizationId,
        defaultOrganizationId,
        activeOrganization,
        detailsById,
        isLoading,
        isLoadingDetails,
        isCreating,
        isSaving,
        isAddingMember,
        isUpdatingMember,
        isRemovingMember,
        isInvitingMember,
        isAcceptingInvite,
        isCancellingInvite,
        isResendingInvite,
        isUploadingLogo,
        isDeleting,
        isSettingDefault,
        error,
        userCan,
        setSelectedOrganization,
        setDefaultOrganization,
        selectDefaultOrganizationIfAvailable,
        selectDefaultOrganizationOnce,
        setSelectedOrganizationFromWorkspace,
        fetchOrganizations,
        fetchOrganization,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        addMember,
        inviteMember,
        updateMember,
        removeMember,
        resolveInvite,
        acceptInvite,
        cancelInvite,
        resendInvite,
        uploadLogo,
    };
});
